import Button from '@mui/material/Button'
import React, { useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { atom, useRecoilState } from 'recoil'
import { cancelEvent, fi } from '../../utils/helpers'
import Loader from '../Loader/Loader'
import GenericModal from './GenericModal'

type ConfirmationModalProps = {
	message: React.ReactNode
	onConfirm: () => Promise<void>
	onCancel?: () => void
}

export const confirmationModalAtom = atom<ConfirmationModalProps | null>({
	key: 'confirmationModalAtom',
	default: null
})

const ConfirmationModal = () => {
	const [value, setValue] = useRecoilState(confirmationModalAtom)
	const [loading, setLoading] = useState(false)

	const onCancel = (evt) => {
		cancelEvent(evt)
		if (!value || loading) return
		if (value.onCancel) {
			value.onCancel()
		}
		setValue(null)
	}

	const onConfirm = async (evt) => {
		cancelEvent(evt)
		if (!value || loading) return
		setLoading(true)
		try {
			await value.onConfirm()
			setValue(null)
		} finally {
			setLoading(false)
		}
	}

	useHotkeys('enter', onConfirm)
	useHotkeys('esc', onCancel)

	const footer = useMemo(() => {
		return (
			<>
				<Button
					variant="contained"
					color="secondary"
					onClick={onCancel}
					disabled={loading}
					data-testid={'modal-cancel'}
					title={'Cancel'}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={onConfirm}
					disabled={loading}
					data-testid={'modal-ok'}
					title={fi(loading, 'Loading, please wait...', 'Ok')}
				>
					{fi(loading, <Loader inverted={true} size={18} />, 'Ok')}
				</Button>
			</>
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, loading])

	if (!value) return null

	return (
		<GenericModal title={'Confirmation'} footer={footer} onClose={onCancel} disableClose={loading}>
			{value.message}
		</GenericModal>
	)
}

export default ConfirmationModal
